import { RootState } from '../types';
import Module from '../interfaces/module.interface';
import BillingService from '@/api/BillingService';
import flagKeys from '@/constants/flagKeys';
import { EBillingStatusTypes } from './businessTypes';
import { IRouterProps } from '@/router/interfaces/router-props.interface';

// The billing route guards read `hasBillingAccess` off the subscription overview
// and run right after the store initializers, so the fetch has to finish before
// navigation resolves — a fire-and-forget request would race them and bounce a
// migrated merchant off their own screen (TWISM-19180). Only the guards that hide
// billing read that answer, so only they pay for it: with `hide-billing-interface`
// off the fetch stays fire-and-forget, exactly as on trunk.
//
// It lives in its own initializer rather than inside the business one so that a
// billing outage retries billing alone: the business stays hydrated, its own
// request is not repeated on every navigation, and INSTORE businesses handed off
// to the host never pay for a request they do not use.
//
// The subscription itself still lives in the business store — only the fetch is
// here — so `getBillingIsPending`, the Locations screens and the postMessage
// bridge keep reading it from where they always did.

// The legacy axios instance is created without a timeout, and the shared
// interceptor retries `timeout` and `Network Error` messages without a cap, so
// setting one on axios would feed that unbounded branch instead of bounding it.
// The bound has to be here, at the call site: `router-methods.ts` awaits the
// initializers between `showLoaderWithLocker` and `next()`, so a request that
// never settles would strand the user on a locked full-screen loader.
//
// It bounds the wait, not the request: the response is still dispatched if it
// lands late, so a slow connection restores the screens on the next navigation
// instead of only on a reload.
export const BILLING_SUBSCRIPTION_TIMEOUT_MS = 3000;

// ...and the number of waits is bounded too. A billing endpoint that is hard down
// stays down for the length of a session, so retrying it on every navigation
// would spend another BILLING_SUBSCRIPTION_TIMEOUT_MS behind the locked loader
// each time, for every manager with the billing module on. After the budget is
// spent the initializer resolves and the cohort reads as unknown, which the
// guards already treat as "not in the cohort" — the same fail-closed direction as
// the skips in the factory. A reload starts a new session and a new budget.
export const MAX_BLOCKING_ATTEMPTS = 3;

let blockingAttempts = 0;

// `disableToastMessage` keeps a billing outage from raising the raw interceptor
// error toast on every session — this runs for every manager with the billing
// module on, whether or not they ever open a billing screen.
const billingService = new BillingService({ disableToastMessage: true });

const withTimeout = <T>(request: Promise<T>): Promise<T> => {
  // Not `number`: the legacy app compiles with Node's typings in scope, so
  // `setTimeout` here is the Node overload returning a `Timeout` handle.
  let timer: ReturnType<typeof setTimeout>;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('Billing subscription request timed out')),
      BILLING_SUBSCRIPTION_TIMEOUT_MS
    );
  });

  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
};

const fetchSubscription = (context: IRouterProps) =>
  billingService
    .getBillingOverviewSubscription()
    .then(({ data: subscription }) =>
      context.store.dispatch('business/SET_BILLING_SUBSCRIPTION', subscription)
    );

export const factory = async (context: IRouterProps) => {
  const { business } = context.store.getters['business/getBusinessInfo'];
  const flags = context.store.getters['flags/getFlags'];

  // The business is only fetched on routes that require it, so an absent one
  // means "not known yet", never "no billing". Throwing leaves this initializer
  // uninitialized — the store initializer swallows the error and runs the
  // factory again on the next navigation, once the business is there.
  if (!business) {
    throw new Error('Business is not loaded yet');
  }

  // A business without the billing module, or with billing unavailable, never
  // gets a subscription: this is a legitimate skip and must resolve, or the
  // initializer would retry it for the rest of the session. Such a business
  // therefore reads as outside the free cohort.
  if (
    !flags.includes(flagKeys.billingModuleV2) ||
    business.subscriptionStatus === EBillingStatusTypes.UNAVAILABLE
  ) {
    return;
  }

  // Nothing reads `hasBillingAccess` while the flag is off, so nothing waits for
  // it: the guards, the Locations screens and `BusinessMenu` all behave exactly
  // as they did on trunk, and a merchant who will never see a billing screen does
  // not pay a serialized round trip on their first navigation.
  if (!flags.includes(flagKeys.hideBillingInterface)) {
    fetchSubscription(context).catch(() => undefined);

    return;
  }

  // Spending the last of the budget resolves rather than throws, which marks the
  // initializer done and takes the fetch off the navigation path for good.
  if (blockingAttempts >= MAX_BLOCKING_ATTEMPTS) {
    return;
  }

  blockingAttempts += 1;

  // Failures and timeouts are left to throw on purpose: that is what keeps the
  // fetch retryable while there is budget left. Until it succeeds the cohort
  // reads as unknown, which the guards treat as "not in the cohort" — the
  // conservative direction while the billing screens are hidden.
  await withTimeout(fetchSubscription(context));
};

const namespaced: boolean = true;

// `requires` makes the business hydrate first whatever the registration order in
// `store/index` happens to be — the resolver marks a dependency initialized after
// running it, so being listed before `business` would cost nothing.
export const billing: Module<{}, RootState> = {
  namespaced,
  initializer: {
    factory,
    on: 'BeforeEachRoute',
    requires: ['BusinessStore'],
    provide: 'BillingSubscriptionStore'
  }
};
